'use client';

/**
 * usePageViewTracking
 *
 * 페이지 뷰 트래킹 훅
 * 컴포넌트 마운트 시 1회만 이벤트 발생
 *
 * @example
 * ```tsx
 * function MissionDetailPage({ missionId }) {
 *   usePageViewTracking('View Mission Detail', {
 *     properties: { mission_id: missionId }
 *   });
 *   // ...
 * }
 * ```
 */
import { useEffect, useRef } from 'react';
import { Analytics } from '../tracker';

interface UsePageViewTrackingOptions {
  /** 이벤트와 함께 전송할 추가 속성 */
  properties?: Record<string, unknown>;
  /** 트래킹 활성화 여부 (기본값: true) */
  enabled?: boolean;
  /** 의존성 배열 - 변경 시 재트래킹 (기본값: []) */
  deps?: unknown[];
}

/**
 * 페이지 뷰 트래킹 훅
 *
 * @param eventName - 발생시킬 이벤트 이름
 * @param options - 옵션
 */
export function usePageViewTracking(
  eventName: string,
  options: UsePageViewTrackingOptions = {},
): void {
  const { properties = {}, enabled = true, deps = [] } = options;

  const trackedRef = useRef(false);
  const eventNameRef = useRef(eventName);
  const propertiesRef = useRef(properties);
  propertiesRef.current = properties;

  // eventName이 변경되면 재트래킹 허용
  if (eventNameRef.current !== eventName) {
    eventNameRef.current = eventName;
    trackedRef.current = false;
  }

  useEffect(() => {
    if (!enabled) {
      return;
    }
    if (trackedRef.current) {
      return;
    }

    Analytics.track(eventName, propertiesRef.current);
    trackedRef.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventName, enabled, ...deps]);
}
